import { minBy } from 'es-toolkit';
import { Immutable } from 'immer';

import { convertFeetToInches } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import { SnappingLevel } from '../../../store/slices/applicationSlice';
import { EntitySnapTargetType } from '../EntitySnapTarget';

export type DistanceSnappingSettings = 'automatic' | Immutable<{ distances: number[]; extended: boolean }>;

const DEFAULT_EXACT_SNAP = Object.freeze({ distances: [0], extended: false }) satisfies DistanceSnappingSettings;

const DEFAULT_INCREMENT_SNAP = Object.freeze({
  distances: [convertFeetToInches(2)],
  extended: true,
}) satisfies DistanceSnappingSettings;

const DEFAULT_PRESETS_SNAP = Object.freeze({
  distances: Object.freeze([0, 2, 4, 6].map(convertFeetToInches)),
  extended: false,
}) satisfies DistanceSnappingSettings;

const SNAP_LEVEL_FINE = Object.freeze({ distances: [1 / 4], extended: true }) satisfies DistanceSnappingSettings;
const SNAP_LEVEL_DEFAULT = Object.freeze({ distances: [1], extended: true }) satisfies DistanceSnappingSettings;
const SNAP_LEVEL_COARSE = Object.freeze({ distances: [12], extended: true }) satisfies DistanceSnappingSettings;

export function getAutomaticDistanceSnappingSettings(
  target: EntitySnapTargetType,
  snappingLevelOverride: SnappingLevel | null,
): DistanceSnappingSettings {
  switch (snappingLevelOverride) {
    case 'fine':
      return SNAP_LEVEL_FINE;
    case 'default':
      return SNAP_LEVEL_DEFAULT;
    case 'coarse':
      return SNAP_LEVEL_COARSE;
  }

  switch (target) {
    case 'structural-wall':
    case 'non-structural-wall':
    case 'beam-bearing-envelope':
    case 'work-line':
    case 'feature-pattern-container-edge':
    case 'insertion-feature-pattern-container-edge':
      return DEFAULT_PRESETS_SNAP;

    case 'truss-envelope':
      return DEFAULT_INCREMENT_SNAP;

    case 'roof':
    case 'ceiling':
    case 'uploaded-line':
      return DEFAULT_EXACT_SNAP;

    default:
      assertExhaustiveSwitch(target);
  }
}

export function applyDistanceSnaps(value: number, settings: DistanceSnappingSettings): number {
  if (settings === 'automatic') {
    throw new Error(
      'Attempted to apply automatic snap to a value.  This should have already been inferred into a real snapping setting.',
    );
  }

  let distances = settings.distances.slice();

  if (settings.extended) {
    distances = distances.concat(distances.map((distance) => roundToIncrement(value, distance)));
  }

  return minBy(distances, (preset) => Math.abs(value - preset))!;
}
